import { GuestModel, useCreateGuest } from 'api-hooks/guest';
import { UsersThreeSVG } from 'common/assets';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import useDismiss from 'hooks/use-dismiss';
import useDrawer, { DrawerProvider } from 'hooks/use-drawer';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import GuestForm from '../components/form';

interface UseCreateGuestActionProps {
  onSuccess?: (guest: GuestModel) => void;
}

export default function useCreateGuestAction(
  props?: UseCreateGuestActionProps,
) {
  const { onSuccess } = props ?? {};
  const onDissmis = useDismiss('guests');
  const { mutateAsync } = useCreateGuest();
  const { t } = useTranslation();

  const permission = useGetCrudPermission('guests');

  const drawer = useDrawer();

  const onCreateGuest = React.useCallback(() => {
    drawer.showCustom({
      render(onClose) {
        return (
          <DrawerProvider>
            <DrawerHeader
              onClose={onClose}
              title={t('common:add_extra', { extra: t('common:guest') })}
            />
            <DrawerBody
              style={{
                maxHeight: 'calc(100dvh - 93px)',
                margin: 0,
                marginLeft: 20,
                marginRight: 20,
                marginBottom: 20,

                overflow: 'auto',
              }}
            >
              <GuestForm
                isLite
                onSubmit={async (values, form) => {
                  const result = await mutateAsync(values);
                  notification.success({
                    message: result.message,
                  });

                  onDissmis();
                  onSuccess?.(result.data);
                  onClose();
                  return result.data;
                }}
              />
            </DrawerBody>
          </DrawerProvider>
        );
      },
      size: 800,
    });
  }, [drawer, mutateAsync, onDissmis, onSuccess, t]);

  const createGuestComponent = permission.create && (
    <Button
      onClick={onCreateGuest}
      startEnhancer={(color, size) => (
        <UsersThreeSVG size={size} color={color} />
      )}
      buttonType="secondary"
    >
      Create Guest
    </Button>
  );

  return {
    onCreateGuest,
    createGuestPermission: permission.create,
    createGuestComponent,
  };
}
